import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { KeyRound, Loader2, Copy, Check, ShieldCheck, AlertCircle } from 'lucide-react';
import { onboardingApi } from '../services/api';
import AfricaNendaLogo from '../components/AfricaNendaLogo';
import SupportContact from '../components/SupportContact';

const Shell = ({ children }) => (
  <div className="min-h-screen bg-gradient-to-br from-primary-50 to-blue-100 flex items-center justify-center p-4">
    <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
      <div className="flex justify-center mb-6"><AfricaNendaLogo className="h-8" /></div>
      {children}
      <SupportContact className="mt-5 text-center"
        lead="Link not working, or need a new API key? Email AfricaNenda at" subject="AfricaNenda IIPS Sandbox account activation" />
    </div>
  </div>
);

export default function Activate() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [pw, setPw]       = useState('');
  const [pw2, setPw2]     = useState('');
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const submit = async () => {
    if (!token) { setError('This activation link is missing its token.'); return; }
    if (pw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (pw !== pw2)   { setError('Passwords do not match.'); return; }
    setBusy(true); setError(null);
    try { setResult(await onboardingApi.activate(token, pw)); }
    catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const copy = (what, text) => { navigator.clipboard.writeText(text); setCopied(what); setTimeout(() => setCopied(false), 2000); };

  const CopyButton = ({ what, text, tone = 'gray' }) => (
    <button onClick={() => copy(what, text)}
      className={`shrink-0 flex items-center gap-1 px-2 py-1.5 text-xs rounded-md border ${tone === 'amber'
        ? 'border-amber-300 text-amber-800 hover:bg-amber-100' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
      {copied === what ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}{copied === what ? 'Copied' : 'Copy'}
    </button>
  );

  if (result) {
    return (
      <Shell>
        <div className="text-center mb-5">
          <div className="inline-flex w-14 h-14 bg-green-100 rounded-full items-center justify-center mb-3"><ShieldCheck className="w-7 h-7 text-green-600" /></div>
          <h1 className="text-lg font-semibold text-gray-900">Account activated</h1>
          <p className="text-sm text-gray-600 mt-1">Sign in with these two details and the password you just set.</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
          {[['tenant', 'Tenant ID', result.tenantId], ['username', 'Username', result.adminUsername]].map(([what, label, value]) => (
            <div key={what}>
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm text-gray-900 bg-white border border-gray-200 rounded px-2 py-1.5 break-all">{value}</code>
                <CopyButton what={what} text={value} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-xs text-amber-800 font-medium mb-1 flex items-center gap-1"><KeyRound className="w-4 h-4" /> Your API key: shown only once, store it now</p>
          <p className="text-xs text-amber-800 mb-2">
            Your systems send it in the <code>X-API-Key</code> header on every API call to the Sandbox. It is not your
            password. If it is lost or exposed, ask AfricaNenda to issue a new one.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-white border border-amber-200 rounded px-2 py-1.5 break-all">{result.apiKey}</code>
            <CopyButton what="key" text={result.apiKey} tone="amber" />
          </div>
        </div>
        <Link to="/login"
          state={{ tenantId: result.tenantId, username: result.adminUsername, message: 'Account activated. Sign in with the password you just set.' }}
          className="block text-center mt-5 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700">Go to sign in</Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="text-center mb-5">
        <div className="inline-flex w-14 h-14 bg-primary-600 rounded-xl items-center justify-center mb-3"><KeyRound className="w-7 h-7 text-white" /></div>
        <h1 className="text-lg font-semibold text-gray-900">Set your password</h1>
        <p className="text-sm text-gray-600 mt-1">Choose a password to activate your participant account.</p>
      </div>
      {error && <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2"><AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{error}</div>}
      <div className="space-y-3">
        <input type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="New password (min 8 chars)"
          className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-primary-500" />
        <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Confirm password"
          onKeyDown={e => e.key === 'Enter' && submit()}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-primary-500" />
        <button onClick={submit} disabled={busy}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Activate account
        </button>
      </div>
    </Shell>
  );
}
